import React, { useEffect } from "react";
import { ShieldCheck, Award, UserCheck } from "lucide-react";

const supportTeam = [
  {
    name: "Omar Al-Harith",
    role: "Principal Product Designer",
    image: "/person1.png",
    message: "Architects visual languages, design systems, and minimalist user interactions across all Quivix products.",
  },
  {
    name: "Elena",
    role: "Lead iOS & Systems Engineer",
    image: "/person2.png",
    message: "Engineers high-performance Swift micro-kernels, native iOS apps, and low-latency client graphics.",
  },
  {
    name: "Hamza Qureshi",
    role: "Global Growth & Positioning Lead",
    image: "/person3.png",
    message: "Drives institutional partnerships, market expansion, and user acquisition strategies across global territories.",
  },
];

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Leadership & Vision — Quivix";
  }, []);

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen pt-36 pb-24 font-sans selection:bg-blue-500/30">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        
        {/* Header Title */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-blue-400">Institutional Mandate</span>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-white mt-3 font-heading">
            Architecting the Future.
          </h1>
          <p className="mt-4 text-base text-[#86868b] leading-relaxed">
            Quivix Inc. was established to solve complex digital challenges through minimalist architecture, extreme performance standards, and uncompromised product execution.
          </p>
        </div>

        {/* Founder / CEO Hero Card */}
        <div className="apple-glass rounded-3xl p-8 sm:p-12 border border-white/[0.08] mb-20 relative overflow-hidden">
          {/* Subtle Ambient Glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative z-10">
            <div className="md:col-span-4 flex justify-center">
              <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-full overflow-hidden border-2 border-white/20 shadow-[0_0_50px_rgba(41,151,255,0.2)] bg-neutral-900 flex items-center justify-center">
                <img
                  src="/salman.png"
                  alt="Salman Khan"
                  className="w-full h-full object-cover"
                  onError={(e) => { 
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center bg-neutral-900">
                  <UserCheck className="w-20 h-20 text-blue-400" />
                </div>
              </div>
            </div>

            <div className="md:col-span-8">
              <span className="text-[11px] font-mono text-blue-400 uppercase tracking-widest bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                Founder & Chief Executive Officer
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 font-heading">Salman Khan</h2>
              <p className="text-xs font-mono text-[#86868b] mt-1">Founder & CEO, QuivixDigital</p>

              <p className="text-xs sm:text-sm text-[#86868b] leading-relaxed mt-4">
                Salman founded QuivixDigital with an execution-first mindset, focusing on practical, high-performance software products that solve real market needs. Under his direction, QuivixDigital operates 18 dedicated servers with sub 20-30ms latency for over 60,000+ active users.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono text-white/80">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> 60k+ Active Users</span>
                <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-blue-400" /> 18 Dedicated Servers</span>
                <span className="text-[10px] bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[#86868b]">
                  Dual HQs: <strong className="text-white font-normal">New Mexico, USA</strong> & <strong className="text-white font-normal">Jeddah, KSA</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Grid - DIRECTLY UNDER CEO */}
        <div className="mb-20">
          <div className="flex items-center justify-between pb-4 border-b border-white/[0.08] mb-8">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-blue-400">Engineering Leadership</span>
              <h2 className="text-xl font-bold text-white font-heading mt-1">Core Staff & Team</h2>
            </div>
            <span className="text-xs font-mono text-[#86868b]">QuivixDigital Key Personnel</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportTeam.map((member, idx) => (
              <div key={member.name} className="apple-glass rounded-3xl p-6 border border-white/[0.08] apple-card-hover flex flex-col justify-between group">
                <div>
                  
                  {/* Circular Profile Picture Frame Container */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-white/20 shadow-md bg-neutral-900 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:border-blue-400 transition-all duration-300">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-full items-center justify-center bg-neutral-900">
                        <UserCheck className="w-7 h-7 text-blue-400" />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-base font-bold text-white font-heading group-hover:text-blue-400 transition-colors">{member.name}</h3>
                      <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20 inline-block mt-1">
                        {member.role}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-[#86868b] leading-relaxed">
                    {member.message}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono text-[#86868b]">
                  <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-emerald-400" /> Verified Core Staff</span>
                  <span>HQ Assigned</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Principles */}
        <div>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-mono uppercase tracking-widest text-indigo-400">Core Pillars</span>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mt-2 font-heading">How We Build Products</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
              <span className="text-blue-400 font-mono text-xs">01</span>
              <h3 className="text-base font-bold text-white font-heading mt-2">Minimalist UI Engineering</h3>
              <p className="text-xs text-[#86868b] mt-2 leading-relaxed">Eliminating clutter to deliver clean, fast, and intuitive user experiences.</p>
            </div>
            <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
              <span className="text-emerald-400 font-mono text-xs">02</span>
              <h3 className="text-base font-bold text-white font-heading mt-2">Zero-Compromise Security</h3>
              <p className="text-xs text-[#86868b] mt-2 leading-relaxed">Building end-to-end verifiability and high-grade data privacy into every layer.</p>
            </div>
            <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
              <span className="text-amber-400 font-mono text-xs">03</span>
              <h3 className="text-base font-bold text-white font-heading mt-2">Ultra-Low Latency</h3>
              <p className="text-xs text-[#86868b] mt-2 leading-relaxed">Hardware-accelerated performance pipelines optimized for edge compute.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutPage;
